import { createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { SCREEN } from '../../../../common/selectCurrencyUnit/constant'
import { calculateDefaultUnit } from '../../../../common/selectCurrencyUnit/slice'
import { CURRENCY } from '../../../../utils/currency'
import { mappingApiTemplate } from '../../../common/helper'
import { companyTypes } from '../constant'
import FinancialAnalysisService from '../core/service'

export const getHighlightThunk = createAsyncThunk(
  'financialAnalysis/GET_HIGHLIGHT',
  async ({ fiscalYearEnd, ...params }, { rejectWithValue, dispatch }) => {
    const response = await FinancialAnalysisService.getHighlight(params)
    if (response.success) {
      const year = new Date(fiscalYearEnd).getFullYear()
      dispatch(
        calculateDefaultUnit({
          screen: SCREEN.FINANCIAL_ANALYSIS,
          getValues: () =>
            response.data
              .filter((item) => item.unit === CURRENCY.VND)
              .map((item) => {
                const exchangeRateType = item.exchangeRateType
                return {
                  value: item.values.find((i) => i.realYear === year)?.value,
                  year,
                  exchangeRateType,
                }
              }),
        }),
      )
      return response.data
    }
    return rejectWithValue(response.errors)
  },
)

export const getTemplatesThunk = createAsyncThunk(
  'financialAnalysis/GET_TEMPLATES',
  async (data, { rejectWithValue }) => {
    try {
      const response = await FinancialAnalysisService.getTemplates(data)
      if (response.success) {
        return {
          ...response,
          data: mappingApiTemplate(response.data || [], data.TemplateType),
        }
      }
      return rejectWithValue(false)
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue(true)
      }
      return rejectWithValue(false)
    }
  },
)

export const getTemplateDetailThunk = createAsyncThunk(
  'financialAnalysis/GET_TEMPLATE_DETAIL',
  async (data, { rejectWithValue }) => {
    const response = await FinancialAnalysisService.getTemplateDetail(
      data.params,
    )
    if (response.success) {
      return { data: response.data, companyType: data.companyType }
    }
    return rejectWithValue(false)
  },
)

export const setDefaultTemplateThunk = createAsyncThunk(
  'financialAnalysis/SET_DEFAULT_TEMPLATE',
  async (data, { dispatch }) => {
    const response = await FinancialAnalysisService.setDefaultTemplate(
      data.param,
    )
    if (response.success) {
      dispatch(getTemplatesThunk(data.paramGet))
      dispatch(getDefaultTemplateThunk(data.paramGet))
    }
  },
)

export const deleteTemplatesThunk = createAsyncThunk(
  'financialAnalysis/DELETE_TEMPLATES',
  async (data, { dispatch }) => {
    const response = await FinancialAnalysisService.deleteTemplates(data.param)
    if (response.success) {
      dispatch(getTemplatesThunk(data.paramGet))
    }
  },
)

export const updateTemplateThunk = createAsyncThunk(
  'financialAnalysis/UPDATE_TEMPLATE',
  async (data, { dispatch }) => {
    const response = await FinancialAnalysisService.updateTemplate(data.param)
    if (response.success) {
      dispatch(getTemplatesThunk(data.paramGet))
    }
  },
)

export const getDefaultTemplateThunk = createAsyncThunk(
  'financialAnalysis/GET_DEFAULT_TEMPLATE',
  async (data, { rejectWithValue }) => {
    try {
      const response = await FinancialAnalysisService.getDefaultTemplate(data)
      if (response.success) {
        return response.data
      }
      return rejectWithValue(false)
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue(true)
      }
      return rejectWithValue(false)
    }
  },
)

export const getDefaultTemplateForLayoutThunk = createAsyncThunk(
  'financialAnalysis/GET_DEFAULT_TEMPLATE_FOR_LAYOUT',
  async (data, { rejectWithValue }) => {
    const response = await FinancialAnalysisService.getDefaultTemplate(data)
    if (response.success) {
      return { result: response.data, companyType: data.TemplateType }
    }
    return rejectWithValue()
  },
)

export const getTemplatesSearchThunk = createAsyncThunk(
  'financialAnalysis/GET_TEMPLATES_SEARCH',
  async (data, { rejectWithValue }) => {
    try {
      const response = await Promise.all(
        companyTypes.map((type) =>
          FinancialAnalysisService.getTemplates({
            FinancialAnalysisTemplateName: data,
            Page: 1,
            PageSize: 3,
            TemplateType: type,
          }),
        ),
      )
      const result = {}
      companyTypes.forEach(
        (type, index) =>
          (result[type] = mappingApiTemplate(response[index].data || [], type)),
      )
      return result
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue(true)
      }
      return rejectWithValue(false)
    }
  },
)

export const getIndicatorPanel = createAsyncThunk(
  'financialAnalysis/GET_INDICATOR_PANEL',
  async (data, { rejectWithValue }) => {
    try {
      const response = await FinancialAnalysisService.getIndicatorPanel(data)
      if (response.success) {
        return response.data || []
      }
      return rejectWithValue(false)
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue(true)
      }
      return rejectWithValue(false)
    }
  },
)
